<template>
  <HeaderBar
    v-if="mainConfigurationPage($route.name)"
    class="hide-for-medium"
    :modifiers="['dark', 'padding-y-medium']"
  >
    <router-link
      :aria-label="$t('general.back')"
      class="header-bar__title header-bar__title--full-width typo-nav-item"
      :to="{ name: mainConfigurationPage($route.name) }"
    >
      <UseSvg
        class="icon"
        name="arrow_left"
      />
      <div class="typo-small-title text-center">
        {{ $t(endPointData.nav) }}
      </div>
    </router-link>

    <UseSvg
      :aria-label="$t('general.search')"
      class="icon"
      name="search"
      @click="toggleSearch"
    />
  </HeaderBar>

  <div
    v-if="subNavLinks"
    :class="{
      'configuration__panel-select': true,
      'configuration__panel-select--padding-x': !isDesktop(),
      'configuration__panel-select--margin-y': !isDesktop(),
    }"
  >
    <Select
      v-model="activeScreen"
      :items="subNavLinks"
      :modifiers="['base-label']"
      placement="bottom-start"
    />
    <p class="configuration__panel-description">
      {{ activeNav(activeScreen)?.description }}
    </p>
  </div>

  <SearchInput
    v-if="!isDesktop() ? showSearch : true"
    v-model="search"
    :placeholder="textData.search"
  />

  <HeaderBar
    :modifiers="[
      'narrow',
      'sticky-with-nav',
      'transparent-on-medium',
      isDesktop() && 'no-padding-x',
      'padding-y',
      'light'
    ]"
  >
    <Select
      v-if="sortByOptions"
      v-model="sortBy"
      :items="sortByOptions"
      :modifiers="['base-label']"
      :placeholder="$t('general.sort_by')"
      placement="bottom-start"
    />
    <template #right>
      <router-link
        v-if="canCreate"
        class="button show-for-medium"
        :to="{ name: endPointData.newFormRoute }"
      >
        <div class="button__icon">
          <PlusIcon />
        </div>
        <div class="button__label">
          {{ textData.new }}
        </div>
      </router-link>
    </template>
  </HeaderBar>
</template>
<script setup>
import { ref, watch } from 'vue';

// Utils
import I18n from 'i18n-js';
import { useRouter } from 'vue-router';
import isDesktop from '@/functions/responsive';
import { mainConfigurationPage } from '@/functions/configuration';

// Components
import SearchInput from '@/components/form/search_input.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Select from '@/components/form/select.vue';

// Svg
import PlusIcon from '@/images/plus-16.svg';
import UseSvg from '@/components/generic/use_svg.vue';

const emit = defineEmits(['searchResults', 'showSearch', 'search', 'sort']);

const props = defineProps({
  /** Hash containing endpoints */
  endPointData: { type: Object, required: true, default: null },
  /** Hash of most text in the form */
  textData: { type: Object, required: true, default: null },
  /** The value of the sub nav link to be pre-selected */
  loadScreen: { type: String, required: false, default: null },
  /** Hash of subNavLinks to load a particular section of the page */
  subNavLinks: { type: Object, required: false, default: null },
  /** Toggles the create button */
  canCreate: { type: Boolean, required: false, default: false },
});

const router = useRouter();
const search = ref(null);
const sortBy = ref(null);
const showSearch = ref(false);
const activeScreen = ref(props.loadScreen);

function activeNav(name) {
  return props.subNavLinks?.find(item => item.value === name);
}

function genericSortOption(value) {
  return {
    label: I18n.t(`general.${value}`),
    value: value,
  };
}

const sortByOptions = [
  ...['alphabetical', 'alphabetical_reverse'].map(genericSortOption),
];

/**
 * Watches for changes on the sort by dropdown
 */
watch(sortBy, () => emit('sort', sortBy.value));

watch(activeScreen, (name) => {
  router.replace({ name });
});

/**
 * Watch and set search
 */
watch(search, () => emit('search', search.value));

function toggleSearch() {
  showSearch.value = !showSearch.value;
  emit('showSearch', showSearch.value);
}
</script>
